import { User } from './user';
import { Course } from './course';
import { Collection } from './collection';
import { Base } from './base-model';
import { Type } from 'class-transformer';
import { Lesson } from './lesson';
import { LessonSong } from './lesson-song';
import { ParentEducationMoment } from './parent-education-moment';
import { Page } from './page';
import { Article } from './article';

export class Teacher extends Base {
  @Type(() => User)
  user: User
  @Type(() => Course)
  courses: Course[]
  collectionUuids: string[]
  @Type(() => ParentEducationMoment)
  customPems: ParentEducationMoment[]
  @Type(() => Page)
  pages: Page[]
  @Type(() => Page)
  resourcePages: Page[]
  @Type(() => Article)
  articles: Article[]

  @Type(() => Lesson)
  lessonsBeingEdited: Map<string, Lesson>  // persisted in local stored; not persisted to server
  @Type(() => Course)
  coursesBeingEdited: Map<string, Course>  // persisted in local stored; not persisted to server

  hash: string

  static defaults(): Teacher {
    const teacher = new Teacher()
    teacher.user = User.defaults()
    teacher.courses = []
    teacher.collectionUuids = []
    teacher.customPems = []
    teacher.hash = ''
    teacher.lessonsBeingEdited = new Map<string, Lesson>()
    teacher.coursesBeingEdited = new Map<string, Course>()
    teacher.pages = []
    teacher.resourcePages = []
    teacher.articles = []
    return teacher
  }

  sendCount = 0

  get isSending(): boolean { 
    return this.sendCount > 0 
  }  // The teacher is in the process of sending and hasn't received a response from the server yet

  get isDirty(): boolean {
    if (this.sendCount < 0) {
      console.log('illegally send count ' + this.sendCount)
    }

    if (this.isSending) {
      return true
    }

    for (const course of this.courses) {
        if (course.isDirty || course.synced == null) {
          return true
        }
        if (course.isDeleted && course.synced <= course.dirtyDate && course.sendDate <= course.dirtyDate) {
          return true
        }


        for (const lesson of course.lessons) {
            if (lesson.isDirty || lesson.synced == null) {
              return true
            }

            if (lesson.isDeleted && lesson.synced <= lesson.dirtyDate && lesson.sendDate <= lesson.dirtyDate) {
              return true
            }

            for (const song of lesson.songs) {
                if (song.isDirty || song.synced == null) {
                  return true
                }

                if (song.isDeleted && song.synced <= song.dirtyDate && song.sendDate <= song.dirtyDate) {
                  return true
                }
            }
        }
    }

    for (const pem of this.customPems) {
      if (pem.isDirty || pem.synced == null) {
        return true
      }
      if (pem.isDeleted && pem.synced <= pem.dirtyDate && pem.sendDate <= pem.dirtyDate) {
        return true
      }
    }

    return false
  }

  set isDirty(_: boolean) {
    console.log('illegally setting isDirty on Teacher')
  }

  getMaximumSyncedValue(): number {
    let maxSynced: number = 0
    for (const course of this.courses) {
      if (course.synced > maxSynced) {
          maxSynced = course.synced
      }
      for (const lesson of course.lessons) {
          if (lesson.synced > maxSynced) {
              maxSynced = lesson.synced
          }
          for (const song of lesson.songs) {
              if (song.synced > maxSynced) {
                  maxSynced = song.synced
              }
          }
      }
    }
    return maxSynced
  }

  // Called when teacher is loaded from storage. This will resend all items
  // that were marked dirty but failed to send before the app quit.
  resetSendDate() {
    this.courses.forEach((course: Course) => {
      course.resetSendDate()
      course.lessons.forEach((lesson: Lesson) => {
        lesson.resetSendDate()
      })
    })
  }

  findCourse(uuid: string): Course {
    return this.courses?.find((course: Course) => uuid && (course.uuid === uuid))
  }

  indexOfCourse(uuid: string): number {
    return this.courses?.findIndex((course: Course) => uuid && (course.uuid === uuid))
  }

  get displayableCourses(): Course[] {
    return this.courses?.filter( (course: Course) => !course.isDeleted )
  }

  get activeCourses(): Course[] {
    // Bug in db where isArchived is being returned as a string rather than a boolean
    return this.displayableCourses?.filter( (course: Course) => typeof course.isArchived === 'string' ? (course.isArchived as any as string) !== '1' : !course.isArchived )
    // return this.displayableCourses.filter( (course: Course) => !course.isArchived )
  }

  get archivedCourses(): Course[] {
      // Bug in db where isArchived is being returned as a string rather than a boolean
    return this.displayableCourses?.filter( (course: Course) => typeof course.isArchived === 'string' ? (course.isArchived as any as string) === '1' : course.isArchived )
    // return this.displayableCourses.filter( (course: Course) => course.isArchived )
  }

  findCustomPem(uuid: string): ParentEducationMoment {
    return this.customPems?.find((pem: ParentEducationMoment) => uuid && (pem.uuid === uuid) )
  }

  collectionsFrom(collections: Collection[]): Collection[] {
    // The teacher object has an array of collectionUuids. Turn those in the corresponding collections. For some reason, the collectionUuids aren't unique.
    const uuidSet = new Set(this.collectionUuids)
    const uuidArray = Array.from(uuidSet)
    return uuidArray.map((uuid: string) => collections.find((collection: Collection) => collection.uuid === uuid)).filter(collection => !!collection)
  }
}
